import type { QuoteSnapshotDTO } from '@/types/inquiry'
import type { PricingBody, PricingConfigDTO, PricingItem, PricingOption, PricingPreset } from '@/types/pricing'

export interface QuoteSelection {
  /** `body.project_types` 의 key */
  type: string
  pages: number
  /** `body.options` 의 key 목록 (기능 + 애드온 모두) */
  options: string[]
  /** `body.support_tiers` 의 key — 티어라서 하나만 */
  support: string
}

export interface QuoteResult {
  oneTime: number
  monthly: number
}

const find = <T extends PricingItem>(items: T[], key: string): T | undefined => items.find((i) => i.key === key)

/** 이 유형에서 고를 수 있는 옵션만 — applies_to 가 비어 있으면 모든 유형에 붙어요. */
export function optionsForType(options: PricingOption[], typeKey: string): PricingOption[] {
  return options.filter((option) => option.applies_to.length === 0 || option.applies_to.includes(typeKey))
}

/** 그 유형에 해당하는 프리셋만 — 견적 화면이 예시를 유형별로 묶어 보여줄 때 써요. */
export function presetsForType(presets: PricingPreset[], typeKey: string): PricingPreset[] {
  return presets.filter((preset) => preset.type === typeKey)
}

export function calculateQuote(sel: QuoteSelection, body: PricingBody): QuoteResult {
  const pages = Math.min(Math.max(0, sel.pages), body.max_pages)
  // 유형에 안 붙는 옵션은 금액에서도 빼요 — 유형을 바꿔도 예전 선택이 남아 있을 수 있어요.
  const usable = optionsForType(body.options, sel.type)
  const optionSum = sel.options.reduce((acc, key) => acc + (find(usable, key)?.amount ?? 0), 0)
  // 페이지 단가는 고른 유형의 값을 써요 — 랜딩 한 장과 웹앱 화면 하나는 공수가 달라요.
  const type = find(body.project_types, sel.type)
  const oneTime = (type?.amount ?? 0) + pages * (type?.page_amount ?? 0) + optionSum
  // 월 지원은 선택한 티어의 금액 하나 — 합산이 아니에요.
  const monthly = find(body.support_tiers, sel.support)?.amount ?? 0
  return { oneTime, monthly }
}

/**
 * 단가가 갱신되면(어드민 수정 후 재조회) 사라진 키를 골라내고 단일 선택은 첫 항목으로 되돌려요.
 * project_types/support_tiers 는 API 가 최소 1개로 검증하지만, 빈 목록이 와도 키를 그대로 두고 버텨요.
 */
export function reconcileSelection(sel: QuoteSelection, body: PricingBody): QuoteSelection {
  const keep = (items: PricingItem[], key: string) => (find(items, key) ? key : (items[0]?.key ?? key))
  const type = keep(body.project_types, sel.type)
  // 유형이 바뀌면 그 유형에 안 붙는 옵션 선택도 같이 버려요.
  const usable = optionsForType(body.options, type)
  return {
    type,
    pages: Math.min(Math.max(0, sel.pages), body.max_pages),
    options: sel.options.filter((key) => find(usable, key) !== undefined),
    support: keep(body.support_tiers, sel.support),
  }
}

/**
 * 프리셋 한 벌을 계산기 선택으로 펼쳐요.
 * `reconcileSelection` 을 태우니 유형에 안 붙는 옵션·페이지 상한이 여기서 정리돼요 —
 * 어드민 검증이 막고 있지만, 프리셋이 저장된 뒤에 옵션의 붙는 유형이 바뀔 수 있어요.
 */
export function presetSelection(preset: PricingPreset, body: PricingBody): QuoteSelection {
  return reconcileSelection(
    { type: preset.type, pages: preset.pages, options: preset.options, support: preset.support },
    body,
  )
}

export interface DiscountedPrice {
  standard: number
  discounted: number
  savings: number
}

/**
 * 표준 구축가에 온라인 할인을 적용하고 상한(clamp)을 씌워요.
 * clamp이 걸리면 앵커(standard)도 상한 기준으로 되맞춰 "N% 할인·절감액" 계산이 일관돼요.
 * 온라인 견적 할인 — 실제로 적용해 진행하는 프로모션 할인(허위 정가 아님).
 */
export function applyOnlineDiscount(standardOneTime: number, rate: number, cap: number): DiscountedPrice {
  const roundToManwon = (v: number) => Math.round(v / 10_000) * 10_000
  const rawDiscounted = roundToManwon(standardOneTime * (1 - rate))
  const discounted = Math.min(rawDiscounted, cap)
  // clamp에 걸리면 표준가를 상한/(1-rate) 로 되맞춰 할인율 관계를 유지.
  const standard = discounted < rawDiscounted ? roundToManwon(cap / (1 - rate)) : standardOneTime
  return { standard, discounted, savings: standard - discounted }
}

/**
 * 구버전 api 응답에는 `presets`·`monthly_free_months` 필드가 없어요 — 롤링 배포 중 신규 web +
 * 구 api 창에서 undefined 를 순회하거나 곱해서(NaN) 터지므로, 페치 경계에서 한 번만 채워요.
 */
export function normalizeConfig(config: PricingConfigDTO): PricingConfigDTO {
  return {
    ...config,
    body: {
      ...config.body,
      presets: config.body.presets ?? [],
      monthly_free_months: config.body.monthly_free_months ?? 0,
    },
  }
}

export const formatWon = (v: number) => `${Math.round(v).toLocaleString('ko-KR')}원`

/**
 * 만원 단위로 떨어지면 짧게 (300,000 → "30만원"), 아니면 `formatWon` 그대로.
 * 월 지원 티어 라벨처럼 문장 안에 들어가는 금액에 써요 — 편집 화면·변경 요약은 정확한 원 단위를 씁니다.
 */
export function formatWonShort(v: number): string {
  const won = Math.round(v)
  if (won === 0 || won % 10_000 !== 0) return formatWon(won)
  return `${(won / 10_000).toLocaleString('ko-KR')}만원`
}

/**
 * 견적 선택 + 계산 결과를 API 스냅샷 형태로 매핑해요.
 * 라벨·금액까지 얼려 담아서, 나중에 어드민에서 항목을 지우거나 단가를 바꿔도 과거 문의가 그대로 읽혀요.
 * `freeMonths` 는 호출부가 넘겨요 — 여기서 `config.body.monthly_free_months` 를 다시 읽으면
 * 월 0원 티어에서 화면(프로모션 미표시)과 스냅샷이 어긋나요.
 */
export function toQuoteSnapshot(
  sel: QuoteSelection,
  config: PricingConfigDTO,
  price: DiscountedPrice,
  result: QuoteResult,
  freeMonths: number,
): QuoteSnapshotDTO {
  const { body } = config
  return {
    config_version: config.version,
    type: sel.type,
    type_label: find(body.project_types, sel.type)?.label ?? sel.type,
    pages: Math.max(0, sel.pages),
    page_amount: find(body.project_types, sel.type)?.page_amount ?? null,
    options: sel.options.flatMap((key) => {
      const option = find(body.options, key)
      return option ? [{ key: option.key, label: option.label, amount: option.amount }] : []
    }),
    support: sel.support,
    support_label: find(body.support_tiers, sel.support)?.label ?? sel.support,
    standard: price.standard,
    discounted: price.discounted,
    monthly: result.monthly,
    monthly_free_months: freeMonths,
  }
}
